import React, { PureComponent } from 'react';
import { Row, Col, Form, FormGroup, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { isURL } from 'validator';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { bool, array, object, func } from 'prop-types';
import Card from '../Card/Card';
import CustomControl from '../CustomControl/CustomControl';
import FlowPostbackMethod from '../Flow/FlowPostbackMethod';
import FlowPostbackUrl from '../Flow/FlowPostbackUrl';
import FlowPostbackStatuses from '../Flow/FlowPostbackStatuses';
import Hint from '../Hint/Hint';
import GlobalPostbackPresets from './GlobalPostbackPresets';

const GLOBAL_USAGE_STATUS = 5;

class GlobalPostbackForm extends PureComponent {
  static propTypes = {
    statuses: array.isRequired,
    enabled: bool,
    loading: bool,
    postback: object,
    onCheckPostback: func.isRequired,
    onSubmit: func.isRequired,
  };

  static defaultProps = {
    statuses: [],
  };

  constructor(props) {
    super(props);

    this.state = {
      enabled: false,
      data: {
        method: 0,
        url: '',
        statuses: [],
      },
    };
  }

  UNSAFE_componentWillReceiveProps({ loading, postback }) {
    if (!loading && postback && postback.url) {
      this.setState((prevState) => ({
        enabled: !!postback.status,
        data: {
          ...prevState.data,
          method: postback.method,
          url: postback.url,
          statuses: postback['pixels_status'] || postback['pixels_status[]'] || [],
        },
      }));
    }
  }

  handleChange = (data) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        ...data,
      },
    }));
  };

  onStatusChange = (statuses) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        statuses,
      },
    }));
  };

  togglePostback = ({ target }) => {
    this.setState(() => ({ enabled: target.checked }));
  };

  toggleGlobalUsage = () => {
    const { statuses } = this.state.data;
    const index = statuses.indexOf(GLOBAL_USAGE_STATUS);
    const updatedStatuses = [];

    if (index === -1) {
      updatedStatuses.push(GLOBAL_USAGE_STATUS);
      updatedStatuses.push(...this.selectAllStatuses());
    } else {
      statuses.splice(index, 1);
      updatedStatuses.push(...statuses);
    }

    this.onStatusChange(updatedStatuses);
  };

  isValid = (url) => url === '' || isURL(url, { require_protocol: true });

  handleSubmit = (e) => {
    e.preventDefault();

    if (this.state.enabled) {
      const { url = '', method = 0, statuses = [] } = this.state.data;

      this.props.onSubmit({
        status: 1,
        url,
        method,
        'pixels_status[]': statuses,
      });
    } else {
      this.props.onSubmit({ status: 0 });
    }
  };

  onPresetApply = (url) => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        url,
      },
    }));
  };

  selectAllStatuses = () => this.props.statuses.map((status) => status.id);

  render() {
    const { enabled, data } = this.state;
    const { loading, statuses } = this.props;
    const globalUsageEnabled = data.statuses.indexOf(GLOBAL_USAGE_STATUS) !== -1;

    return (
      <Form className="global-postback-form" onSubmit={this.handleSubmit}>
        <Card loading={loading}>
          <Card.Body>
            <FormGroup className="postback-enable">
              <CustomControl
                id="global-postback"
                className="custom-checkbox-switch"
                label={
                  <div className="custom-checkbox-switch-text">
                    <FormattedMessage
                      id="postback.global.enable"
                      defaultMessage="Enable global postback"
                    />
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <Hint id="global-postback-hint" placement="right" delay={250}>
                        <FormattedHTMLMessage
                          id="postback.global.hint-1"
                          defaultMessage="A&nbsp;global postback is&nbsp;an&nbsp;additional parameter, which lets you
                  collecting information about every conversion you&rsquo;ve made and aggregate your
                  very own statistics via self-hosted statistics systems or&nbsp;third-party
                  trackers."
                          tagName="p"
                        />
                        <FormattedHTMLMessage
                          id="postback.global.hint-2"
                          defaultMessage="When our statistics tracks a&nbsp;conversion, the system will send GET
                  or&nbsp;POST request to&nbsp;the url you specified in&nbsp;&laquo;Postback
                  URL&raquo; parameter."
                          tagName="p"
                        />
                      </Hint>
                    </span>
                  </div>
                }
                value="1"
                onChange={this.togglePostback}
                checked={enabled}
                inline
              />
            </FormGroup>

            {enabled && <GlobalPostbackPresets onPresetApply={this.onPresetApply} />}

            <fieldset disabled={!enabled}>
              <Row>
                <Col lg="2">
                  <FlowPostbackMethod
                    method={data.method}
                    onPostbackMethodChange={this.handleChange}
                  />
                </Col>

                <Col lg="10">
                  <FlowPostbackUrl
                    url={data.url}
                    onUrlChange={this.handleChange}
                    onCheckPostback={() => this.props.onCheckPostback(data)}
                  />
                </Col>
              </Row>

              <div className="postback-extra-settings">
                <Row>
                  <Col lg="4" xl="3">
                    <CustomControl
                      id="global-usage"
                      className="custom-control-xs no-margin"
                      label={
                        <FormattedMessage
                          id="postback.global.use-for-all"
                          defaultMessage="Use for all flows"
                        />
                      }
                      value="1"
                      onChange={this.toggleGlobalUsage}
                      checked={globalUsageEnabled}
                      inline
                    />

                    <Hint id="global-postback-usage" placement="right" delay={250}>
                      <FormattedMessage
                        id="postback.global.use-for-all.hint"
                        defaultMessage="The global postback will be working on every flow you created, disabling
                          settings of postback you specified in the flow before"
                      />
                    </Hint>
                  </Col>

                  <Col lg="8" xl="9">
                    <FlowPostbackStatuses
                      statuses={statuses}
                      selected={data.statuses}
                      onStatusChange={this.onStatusChange}
                    />
                  </Col>
                </Row>
              </div>
            </fieldset>
          </Card.Body>

          <Card.Footer>
            <div className="postback-links">
              <Link to="/tools/global-postback/logs">
                <FormattedMessage id="flow.form.postback.logs" defaultMessage="Logs" />
              </Link>
              <Button
                className="form-group-btn btn-md"
                color="primary"
                disabled={loading || !this.isValid(data.url)}
              >
                <FormattedMessage id="main.save" defaultMessage="Save" />
              </Button>
            </div>
          </Card.Footer>
        </Card>

        {/* <Row>
          <Col>
            <FormGroup>
              <Button
                className="btn-lead"
                size="lg"
                color="primary"
                disabled={loading || !this.isValid(data.url)}
              >
                <FormattedMessage id="main.save" defaultMessage="Save" />
              </Button>
            </FormGroup>
          </Col>
        </Row> */}
      </Form>
    );
  }
}

export default GlobalPostbackForm;
